import DeleteIcon from "@mui/icons-material/Delete";
import InfoIcon from "@mui/icons-material/Info";
import {
  Alert,
  Box,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import * as React from "react";
import { Link } from "react-router-dom";

import { useNavigation } from "@/providers";
import {
  AlertDialog,
  RobotoButton,
  RobotoLinkHoverUnderline,
} from "@/shared/components";
import { SystemUserOnly } from "@/shared/components/SystemUserOnly";
import {
  ActionRecord,
  ComputeRequirements,
  ContainerParameters,
} from "@/shared/domain/actions";
import { UpdateActionRequest } from "@/shared/domain/actions/ActionRecord";
import {
  useDeleteAction,
  useUpdateAction,
} from "@/shared/domain/actions/hooks";
import { OrganizationTier, OrgRecord } from "@/shared/domain/orgs";
import { ErrorMonitoringService } from "@/shared/services";

import { ActionConfigurationGuidance } from "./ActionConfigurationGuidance";
import ChangeActionAccessibilityButton from "./ChangeActionAccessibilityButton";
import { DownloadInputsSwitch } from "./DownloadInputsSwitch";
import { EditableActionImage } from "./EditableActionImage";
import EditableActionParams from "./EditableActionParams";
import EditableComputeReqs from "./EditableComputeReqs";
import EditableContainerParams from "./EditableContainerParams";
import { EditableListItem } from "./EditableListItem";
import { EditableTimeout } from "./EditableTimeout";
import { ImageGuidance } from "./ImageGuidance";
import InvokeActionButton from "./InvokeActionButton";
import { UpdateActionMetadata } from "./UpdateActionMetadata";
import { UpdateActionTags } from "./UpdateActionTags";

interface ActionDetailsProps {
  action: ActionRecord;
  currentOrg: OrgRecord | null;
  canEdit?: boolean;
}

const noop = async () => {};

export const ActionDetails: React.FC<ActionDetailsProps> = ({
  action,
  currentOrg,
  canEdit = false,
}) => {
  const deleteActionMutation = useDeleteAction();
  const updateActionMutation = useUpdateAction();

  const [errorText, setErrorText] = React.useState<string | null>(null);
  const [alertDialogOpen, setAlertDialogOpen] = React.useState<boolean>(false);
  const [alertDialogTitle, setAlertDialogTitle] = React.useState<string>("");
  const [alertDialogText, setAlertDialogText] = React.useState<string>("");
  const [alertDialogContent, setAlertDialogContent] =
    React.useState<React.ReactNode>(undefined);

  const [alertDialogAction, setAlertDialogAction] =
    React.useState<() => Promise<void>>();

  const [alertDialogSuppressOptions, setAlertDialogSuppressOptions] =
    React.useState<boolean>(false);

  const { goto } = useNavigation();

  const theme = useTheme();

  function handleActionUpdate(updates: UpdateActionRequest) {
    if (!currentOrg?.org_id) {
      return;
    }

    updateActionMutation.mutate(
      {
        name: action.name,
        resourceOwnerId: currentOrg.org_id,
        updates,
      },
      {
        onError: (err) => setErrorText(err.message),
      },
    );
  }

  function wrapActionWithDialog(
    title: string,
    text: string,
    action: () => Promise<void>,
    content?: React.ReactNode,
    suppressOptions: boolean = false,
  ) {
    setAlertDialogTitle(title);
    setAlertDialogText(text);
    setAlertDialogContent(content);
    setAlertDialogAction(() => action);
    setAlertDialogOpen(true);
    setAlertDialogSuppressOptions(suppressOptions);
  }

  const getRepoIdentifier = (url: string) => {
    const parts = url.split("/");
    // https://github.com/account/repo/...
    // extract "account/repo"
    return parts[3] + "/" + parts[4];
  };

  const githubUrl =
    typeof action.metadata?.github_url === "string" &&
    action.metadata.github_url.length
      ? action.metadata.github_url
      : null;

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          maxWidth: "2000px",
        }}
      >
        <Box
          sx={{
            width: {
              xs: "100%",
              sm: "50%",
            },
          }}
        >
          {errorText && (
            <Box sx={{ mt: theme.spacing(1) }}>
              <Alert severity="error">{errorText}</Alert>
            </Box>
          )}

          <List>
            <ListItem>
              <ListItemText primary={"Name"} secondary={action.name} />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={"Created By"}
                secondary={
                  <RobotoLinkHoverUnderline to={`/users/${action.created_by}`}>
                    {action.created_by}
                  </RobotoLinkHoverUnderline>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={"Modified"}
                secondaryTypographyProps={{
                  component: "span",
                }}
                secondary={
                  <>
                    <Typography variant="body2">
                      {new Date(action.modified || "").toLocaleString() +
                        " by "}
                      <RobotoLinkHoverUnderline
                        to={`/users/${action.modified_by}`}
                      >
                        {action.modified_by}
                      </RobotoLinkHoverUnderline>
                    </Typography>
                  </>
                }
              />
            </ListItem>

            <Box
              sx={{
                display: {
                  sm: "block",
                  md: "none",
                },
              }}
            >
              <EditableListItem
                title="Summary"
                field="short_description"
                value={action.short_description}
                onChange={handleActionUpdate}
                setErrorText={setErrorText}
                renderMarkdown={false}
                canEdit={canEdit}
                maxLength={140}
              />

              <EditableListItem
                title="Description"
                field="description"
                value={action.description}
                onChange={handleActionUpdate}
                setErrorText={setErrorText}
                renderMarkdown={true}
                canEdit={canEdit}
              />
            </Box>

            <EditableActionImage
              imageUri={action.uri}
              currentOrg={currentOrg}
              onChange={handleActionUpdate}
              setErrorText={setErrorText}
              onHelperClick={() => {
                wrapActionWithDialog(
                  "Container image",
                  "",
                  noop,
                  <ImageGuidance />,
                  true,
                );
              }}
              canEdit={canEdit}
            />

            {currentOrg?.tier === OrganizationTier.Premium ? (
              <EditableTimeout<UpdateActionRequest>
                value={action.timeout}
                onChange={handleActionUpdate}
                setErrorText={setErrorText}
                renderMarkdown={false}
                canEdit={canEdit}
              />
            ) : (
              <ListItem>
                <ListItemText
                  primary={"Timeout"}
                  secondaryTypographyProps={{
                    component: "span",
                  }}
                  secondary={
                    action.timeout ? `${action.timeout} minutes` : "None"
                  }
                />
              </ListItem>
            )}

            <EditableActionParams<UpdateActionRequest>
              value={action.parameters || []}
              onChange={handleActionUpdate}
              setErrorText={setErrorText}
              canEdit={canEdit}
            />

            <EditableContainerParams<UpdateActionRequest>
              title="Container Parameters"
              field="container_parameters"
              value={action.container_parameters || ({} as ContainerParameters)}
              onChange={handleActionUpdate}
              setErrorText={setErrorText}
              canEdit={canEdit}
            />

            <EditableComputeReqs<UpdateActionRequest>
              title="Compute Requirements"
              field="compute_requirements"
              value={action.compute_requirements || ({} as ComputeRequirements)}
              onChange={handleActionUpdate}
              setErrorText={setErrorText}
              canEdit={canEdit}
            />

            {!action.inherits && (
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ component: "span" }}
                  sx={{ mt: theme.spacing(0.5) }}
                  primary={
                    <>
                      <span>Action Configuration</span>

                      <IconButton
                        sx={{
                          p: "5px",
                          verticalAlign: "middle",
                          ml: theme.spacing(0.25),
                        }}
                        onClick={() => {
                          setAlertDialogOpen(true);
                          setAlertDialogTitle("Action Configuration");
                          setAlertDialogText("");
                          setAlertDialogContent(
                            <ActionConfigurationGuidance />,
                          );
                        }}
                      >
                        <InfoIcon
                          color="primary"
                          sx={{
                            fontSize: "1.1rem",
                            cursor: "pointer",
                          }}
                        />
                      </IconButton>
                    </>
                  }
                  secondaryTypographyProps={{ component: "span" }}
                  secondary={
                    <FormControlLabel
                      control={
                        <DownloadInputsSwitch
                          checked={action.requires_downloaded_inputs ?? true}
                          onChange={handleActionUpdate}
                          setErrorText={setErrorText}
                        />
                      }
                      label={
                        <Typography variant="body2">Download Inputs</Typography>
                      }
                    />
                  }
                />
              </ListItem>
            )}

            <ListItem>
              <ListItemText
                primary={"Tags"}
                secondaryTypographyProps={{
                  component: "span",
                  mt: theme.spacing(0.5),
                }}
                secondary={
                  <UpdateActionTags
                    initialTags={action.tags || []}
                    onChange={handleActionUpdate}
                    setErrorText={setErrorText}
                    canEdit={canEdit}
                  />
                }
              />
            </ListItem>

            <ListItem>
              <ListItemText
                primary={"Metadata"}
                secondaryTypographyProps={{
                  component: "span",
                }}
                secondary={
                  <UpdateActionMetadata
                    initialMetadata={action.metadata || {}}
                    onChange={handleActionUpdate}
                    setErrorText={setErrorText}
                    canEdit={canEdit}
                  />
                }
              />
            </ListItem>
          </List>
        </Box>
        <Box
          component={Paper}
          variant="outlined"
          sx={{
            mt: theme.spacing(1.5),
            padding: theme.spacing(1),
            pt: 0,
            background: "none",
            width: "48%",
            height: "fit-content",
            display: {
              xs: "none",
              md: "block",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: theme.spacing(2),
              borderBottom: "1px solid " + theme.palette.divider,
              ml: theme.spacing(1),
              mr: theme.spacing(1),
              p: theme.spacing(1),
              pt: theme.spacing(2),
              pb: theme.spacing(2),
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: theme.spacing(2),
              }}
            >
              <InvokeActionButton action={action} />
              {action.org_id === currentOrg?.org_id && (
                <RobotoButton
                  disabled={
                    canEdit === undefined || deleteActionMutation.isPending
                  }
                  eventName="ActionDeleted"
                  eventProperties={{ name: action.name }}
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={() =>
                    wrapActionWithDialog(
                      "Delete Action",
                      `Are you sure you want to delete ${action.name}?`,
                      async function deleteAction() {
                        try {
                          await deleteActionMutation.mutateAsync({
                            name: action.name,
                            resourceOwnerId: currentOrg.org_id,
                          });
                          setErrorText(null);
                          goto.actions();
                        } catch (error) {
                          const err =
                            error instanceof Error
                              ? error
                              : new Error(String(error));
                          ErrorMonitoringService.captureError(err, { action });
                          setErrorText(err.message);
                        }
                      },
                    )
                  }
                >
                  Delete
                </RobotoButton>
              )}
              <SystemUserOnly>
                <ChangeActionAccessibilityButton
                  action={action}
                  onSuccess={() => {
                    window.location.reload();
                  }}
                  onError={(error) => {
                    setErrorText(error.message);
                  }}
                />
              </SystemUserOnly>
            </Box>

            {githubUrl !== null && (
              <Box
                sx={{
                  display: "flex",
                  gap: theme.spacing(1.5),
                  alignItems: "center",
                }}
              >
                <Link
                  target="_blank"
                  style={{ display: "flex" }}
                  to={githubUrl}
                >
                  <img
                    alt="GitHub Logo"
                    style={{ width: "28px", height: "28px" }}
                    src={
                      theme.palette.mode === "dark"
                        ? "/github-mark-white.svg"
                        : "/github-mark.svg"
                    }
                  />
                </Link>
                <Link
                  target="_blank"
                  style={{ display: "flex" }}
                  to={githubUrl}
                >
                  <img
                    src={
                      "https://img.shields.io/github/stars/" +
                      getRepoIdentifier(action.metadata["github_url"])
                    }
                    height={20}
                    alt="GitHub Stars"
                  />
                </Link>
              </Box>
            )}
          </Box>

          <List>
            <EditableListItem
              title="Summary"
              field="short_description"
              value={action.short_description}
              onChange={handleActionUpdate}
              setErrorText={setErrorText}
              renderMarkdown={false}
              canEdit={canEdit}
              maxLength={140}
            />
            <EditableListItem
              title="Description"
              field="description"
              value={action.description}
              onChange={handleActionUpdate}
              setErrorText={setErrorText}
              renderMarkdown={true}
              canEdit={canEdit}
            />
          </List>
        </Box>
      </Box>

      <AlertDialog
        dialogOpen={alertDialogOpen}
        handleClose={() => {
          setAlertDialogOpen(false);
        }}
        dialogTitle={alertDialogTitle}
        dialogText={alertDialogText}
        dialogContent={alertDialogContent}
        dialogAction={alertDialogAction}
        suppressOptions={alertDialogSuppressOptions}
      />
    </>
  );
};
